import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CRITERIA = [
  { n: '01', t: 'Craft-led enterprise', d: 'An established entrepreneur, studio, collective or social enterprise rooted in handcrafted products.' },
  { n: '02', t: 'Authentic provenance', d: 'A real connection to local makers, traditional skills, regional materials or living craft communities.' },
  { n: '03', t: 'Reliable quality', d: 'Consistent finish, responsible production and the ability to meet agreed volumes and timelines.' },
  { n: '04', t: 'Design openness', d: 'A willingness to refine product, packaging and presentation for contemporary luxury markets.' },
  { n: '05', t: 'Ethical practice', d: 'Transparent sourcing, fair artisan compensation and dignity throughout the production chain.' },
  { n: '06', t: 'Local leadership', d: 'The ambition to grow your own enterprise while opening opportunity for the artisan ecosystem around you.' },
];

const CHIPS = [
  'Collection development',
  'Material & finish',
  'Contemporary application',
  'Luxury packaging',
  'Pricing & placement',
  'Market readiness',
];

const JOURNEY = [
  { t: 'Discover', d: 'We identify credible craft entrepreneurs and distinctive regional capabilities.' },
  { t: 'Develop', d: 'Design mentors strengthen product, presentation and commercial readiness.' },
  { t: 'Curate', d: 'Approved pieces become coherent collections for a market, occasion and audience.' },
  { t: 'Connect', d: 'Chaleur brings visibility across digital, corporate, retail and collaborative channels.' },
  { t: 'Lift', d: 'Every sale circulates value through the maker, partner, platform and social-impact pool.' },
];

const SHARES = [
  { t: 'Maker', d: 'Fair, direct compensation to the artisan or artisan collective creating the piece.' },
  { t: 'Partner', d: 'Local channel partner who develops, curates and represents the enterprise on the ground.' },
  { t: 'Platform', d: 'Chaleur — curation, storytelling, mentoring, discovery and global commerce.' },
  { t: 'Social-impact', d: 'A shared pool that reinvests in craft communities, next-generation apprentices and heritage.' },
];

const MARKETS = [];

const BecomePartnerPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', country: '', city: '',
    business_type: '', tier_interest: '', website: '', annual_revenue: '',
    years_in_business: '', current_luxury_brands: '', message: '',
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Full-name check: at least two words (first + last)
    const nameParts = form.contact_name.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length < 2) {
      toast.error('Please enter your full name (first and last).');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partners/apply`, form);
      navigate('/partner-thank-you', { replace: true });
    } catch {
      toast.error('Something went wrong. Please try again or write to partners@chaleur.online.');
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24" data-testid="become-partner-page">

      {/* Hero — banner with text overlay */}
      <section className="relative w-full min-h-[70vh] flex items-end" data-testid="partner-hero">
        <img
          src="/images/partners/chaleur-hero.png"
          alt="Artisan hands finishing a handcrafted luxury gift"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="partner-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 chaleur-container py-20 md:py-28 text-background">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase text-background/80 mb-6 animate-fade-in-up">
              Chaleur Global Channel Partners
            </p>
            <h1 className="editorial-italic text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Local hands.<br /> Global stories.
            </h1>
            <p className="text-base md:text-lg text-background/85 leading-relaxed max-w-xl mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              A curated global platform for established craft entrepreneurs — bringing regional
              artistry to discerning markets while creating shared prosperity.
            </p>
            <a
              href="#apply"
              className="inline-flex items-center gap-2 text-sm tracking-widest uppercase border-b border-background pb-1 hover:opacity-70 transition-opacity animate-fade-in-up"
              style={{ animationDelay: '0.3s' }}
              data-testid="partner-hero-apply-btn"
            >
              Partner with us <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Proposition */}
      <section className="border-y border-border/60 bg-muted/30 py-24 md:py-32">
        <div className="chaleur-container grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">The proposition</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight mb-10">
              A marketplace for craftsmanship, elevated through design — and built to <em>lift as it grows.</em>
            </h2>
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg mb-6 max-w-3xl">
              Partners remain independent creators and local market builders. Chaleur brings
              curation, discoverability, mentoring, storytelling and access to premium gifting demand.
            </p>
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg max-w-3xl">
              Every approved product carries its maker, place and purpose forward. Luxury becomes
              more meaningful when the value it creates is shared.
            </p>
          </div>
        </div>
      </section>

      {/* Who should apply */}
      <section className="chaleur-container py-24 md:py-32">
        <div className="mb-16 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">Who should apply</p>
          <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight">
            Established locally.<br /> Ready to travel globally.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-14">
          {CRITERIA.map((c) => (
            <div key={c.n} data-testid={`partner-criteria-${c.n}`}>
              <p className="text-xs tracking-widest text-muted-foreground mb-4">{c.n}</p>
              <h3 className="text-xl mb-4">{c.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Designed to develop */}
      <section className="bg-foreground text-background py-24 md:py-32">
        <div className="chaleur-container grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase text-background/60 mb-6">Designed to develop — not dilute</p>
            <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
              Your craft.<br /> Sharpened for new markets.
            </h2>
            <p className="text-background/70 leading-relaxed max-w-md mb-8">
              Qualified designers and category specialists mentor selected partners while protecting
              the cultural identity and authorship of every craft.
            </p>
            <div className="flex flex-wrap gap-3">
              {CHIPS.map((chip) => (
                <span
                  key={chip}
                  className="border border-background/30 px-4 py-2 text-xs tracking-wide"
                  data-testid={`partner-chip-${chip.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
          <div className="md:col-span-7">
            <img
              src="/images/partners/craft-basket.jpg"
              alt="Handwoven wicker basket centerpiece with white lilies on a candlelit dining table — a signature Chaleur table styling"
              className="w-full h-auto object-cover"
              data-testid="partner-craft-image"
            />
          </div>
        </div>
      </section>

      {/* Partner journey */}
      <section className="chaleur-container py-24 md:py-32">
        <div className="mb-20 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">The partner journey</p>
          <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight">
            From regional craft to a globally curated collection.
          </h2>
        </div>
        <div className="grid md:grid-cols-5 gap-10 md:gap-6">
          {JOURNEY.map((s, i) => (
            <div key={s.t} className="relative">
              <div className="mb-6">
                <span className="editorial-italic text-3xl">{i + 1}</span>
              </div>
              <h3 className="text-xl mb-4">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Lift as You Gift */}
      <section className="bg-muted/30 border-y border-border/60 py-24 md:py-32">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-4">
              <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Lift as You Gift</p>
            </div>
            <div className="md:col-span-8">
              <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
                Four equal<br /> ways forward.
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-3xl">
                Chaleur replaces a conventional marketplace commission mindset with a transparent
                shared-value model.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {SHARES.map((s) => (
              <div key={s.t} className="bg-background border border-border p-8" data-testid={`partner-share-${s.t.toLowerCase().replace('-', '_')}`}>
                <div className="editorial-italic text-5xl mb-4">25%</div>
                <h3 className="text-lg mb-3">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed mt-12 max-w-3xl">
            Equal distribution applies to distributable profit after disclosed direct costs.
            Country-specific commercial, tax and social-impact structures will be confirmed in each
            partner agreement.
          </p>
        </div>
      </section>

      {/* Apply / form */}
      <section id="apply" className="bg-foreground text-background py-24 md:py-32">
        <div className="chaleur-container max-w-3xl">
          <div className="mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-background/60 mb-6">Become a Chaleur channel partner</p>
            <h2 className="editorial-italic text-3xl md:text-4xl lg:text-5xl leading-tight mb-8">
              Bring your region&apos;s finest craft to the world.
            </h2>
            <p className="text-background/70 leading-relaxed max-w-2xl">
              Tell us about your enterprise, your artisan community and the craft story you are
              ready to grow.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-6" data-testid="partner-form">
            <div>
              <Label className="text-background/80 mb-2 block">Company / enterprise name *</Label>
              <Input required value={form.company_name} onChange={(e)=>update('company_name', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-company-name" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Full name *</Label>
              <Input required value={form.contact_name} onChange={(e)=>update('contact_name', e.target.value)} placeholder="First and last name" className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-contact-name" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e)=>update('email', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-email" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Phone *</Label>
              <Input required value={form.phone} onChange={(e)=>update('phone', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-phone" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Country *</Label>
              <Input required value={form.country} onChange={(e)=>update('country', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-country" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">City / region</Label>
              <Input value={form.city} onChange={(e)=>update('city', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-city" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Enterprise type *</Label>
              <Select value={form.business_type} onValueChange={(v)=>update('business_type', v)}>
                <SelectTrigger className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-business-type">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrepreneur">Craft entrepreneur / studio</SelectItem>
                  <SelectItem value="collective">Artisan collective</SelectItem>
                  <SelectItem value="social-enterprise">Social enterprise / NGO</SelectItem>
                  <SelectItem value="designer">Designer-led label</SelectItem>
                  <SelectItem value="distributor">Distributor / agent</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Region of interest</Label>
              <Select value={form.tier_interest} onValueChange={(v)=>update('tier_interest', v)}>
                <SelectTrigger className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-tier-interest">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="india">India</SelectItem>
                  <SelectItem value="uae-gcc">UAE & GCC</SelectItem>
                  <SelectItem value="uk-europe">UK & Europe</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Website / Instagram *</Label>
              <Input required value={form.website} onChange={(e)=>update('website', e.target.value)} placeholder="https:// or @handle" className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-website" />
            </div>
            <div>
              <Label className="text-background/80 mb-2 block">Years established</Label>
              <Input value={form.years_in_business} onChange={(e)=>update('years_in_business', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-years" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-background/80 mb-2 block">Craft, region and artisan community</Label>
              <Input value={form.current_luxury_brands} onChange={(e)=>update('current_luxury_brands', e.target.value)} placeholder="e.g. pashmina from Kashmir with 40+ weaver families" className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-brands" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-background/80 mb-2 block">Tell us your story</Label>
              <Textarea rows={4} value={form.message} onChange={(e)=>update('message', e.target.value)} className="bg-transparent border-background/30 text-background rounded-none" data-testid="partner-message" />
            </div>
            <div className="md:col-span-2 pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button type="submit" size="lg" disabled={submitting} className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto px-10" data-testid="partner-submit">
                {submitting ? 'Sending…' : 'Begin a conversation'}
                <ArrowUpRight className="ml-2 w-4 h-4" />
              </Button>
              <p className="text-xs text-background/60 leading-relaxed">
                By submitting, you agree to be contacted by our partner desk.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BecomePartnerPage;
